/**
 * One implementation of every document export, for the editor menu and the
 * native File menu. They used to be able to drift because the File menu had
 * nowhere to call.
 */

import { renderMarkdown } from "@/lib/markdown";
import { parseSlides } from "@/lib/slides";
import {
  downloadBlob,
  exportImageZip,
  exportMarp,
  exportPptx,
  exportSlidesPdf,
  safeFileName,
} from "@/lib/slide-export";
import { manuscriptPrintHtml, printHtml } from "@/lib/print";
import type { DesktopExportKind } from "@/lib/desktop-session";
import type { SlideStyle } from "@/lib/slide-themes";

export async function runDocumentExport(options: {
  kind: DesktopExportKind;
  title: string;
  content: string;
  slideStyle: SlideStyle;
}): Promise<void> {
  const { kind, title, content, slideStyle } = options;
  const { manuscript, slides } = parseSlides(content);

  switch (kind) {
    case "manuscript-md":
      await downloadBlob(
        new Blob([manuscript], { type: "text/markdown;charset=utf-8" }),
        `${safeFileName(title)}.md`,
      );
      return;
    case "source-md":
      await downloadBlob(
        new Blob([content], { type: "text/markdown;charset=utf-8" }),
        `${safeFileName(title)}-full.md`,
      );
      return;
    case "manuscript-print":
      await printHtml(manuscriptPrintHtml(title, renderMarkdown(manuscript)));
      return;
    case "pptx":
      await exportPptx(slides, title, slideStyle);
      return;
    case "images":
      await exportImageZip(slides, title, slideStyle);
      return;
    case "slides-pdf":
      await exportSlidesPdf(slides, title, slideStyle);
      return;
    case "marp":
      await exportMarp(slides, title, slideStyle);
      return;
  }
}
